import { PendingAccount } from 'types/accountTypes'
import { useContext, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { approveUser } from 'api/accounts'
import { SnackbarContext } from 'contexts/SnackbarContext'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  Stack,
  Typography
} from '@mui/material'
import DiscountRateBuilder from 'components/DiscountRateBuilder'

export interface Address {
  firstName: string
  lastName: string
  addressLine1: string
  addressLine2?: string
  city: string
  state: string
  country?: string
  zipCode: string
  phoneNumber: string
}

interface ApproveUserFormProps {
  data: PendingAccount
}

const normalizeAddressValue = (value?: string | null) => value?.trim() || ''

const areAddressesSame = (billingAddress: Address, shippingAddress: Address) =>
  (
    [
      'firstName',
      'lastName',
      'addressLine1',
      'addressLine2',
      'city',
      'state',
      'country',
      'zipCode',
      'phoneNumber'
    ] as const
  ).every(
    field =>
      normalizeAddressValue(billingAddress[field]) ===
      normalizeAddressValue(shippingAddress[field])
  )

const ApproveUserForm = ({ data }: ApproveUserFormProps) => {
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isTaxable, setIsTaxable] = useState<string>(
    !data.isTaxable ? 'false' : 'true'
  )
  const discountRatesRef = useRef<Record<string, number>>({})
  const navigate = useNavigate()
  const { showMessage } = useContext(SnackbarContext)

  const billingAddress: Address = {
    firstName: data.billingFirstName,
    lastName: data.billingLastName,
    addressLine1: data.billingAddressLine1,
    addressLine2: data.billingAddressLine2,
    city: data.billingCity,
    state: data.billingState,
    country: data.billingCountry,
    zipCode: data.billingZipCode,
    phoneNumber: data.billingPhoneNumber
  }

  const shippingAddress: Address = {
    firstName: data.shippingFirstName,
    lastName: data.shippingLastName,
    addressLine1: data.shippingAddressLine1,
    addressLine2: data.shippingAddressLine2,
    city: data.shippingCity,
    state: data.shippingState,
    country: data.shippingCountry,
    zipCode: data.shippingZipCode,
    phoneNumber: data.shippingPhoneNumber
  }

  const isBillingAndShippingAddressSame = areAddressesSame(
    billingAddress,
    shippingAddress
  )

  const handleApprove = async () => {
    setError('')
    setIsLoading(true)

    const result = await approveUser({
      email: data.email,
      companyName: data.companyName,
      phoneNumber: data.phoneNumber,
      ein: data.taxId,
      salesTaxAccount: data.salesTaxAccount,
      discountRates: discountRatesRef.current,
      isTaxable: isTaxable === 'true',
      isBillingAndShippingAddressSame,
      billingAddress,
      shippingAddress: isBillingAndShippingAddressSame
        ? undefined
        : shippingAddress
    })

    if (result.success) {
      showMessage(result.message)
      navigate('/accounts')
      return
    }

    setError(result.message)
    setIsLoading(false)
  }

  return (
    <Box mt={4}>
      <Typography variant='h6'>Approve Account</Typography>

      {error && <Alert severity='error'>{error}</Alert>}

      <Box mt={2}>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          mb={2.5}
        >
          <Typography variant='body2' color='text.secondary'>
            Taxable
          </Typography>
          <FormControl size='small' sx={{ minWidth: 100 }}>
            <Select
              value={isTaxable}
              onChange={e => setIsTaxable(e.target.value)}
            >
              <MenuItem value='true'>Yes</MenuItem>
              <MenuItem value='false'>No</MenuItem>
            </Select>
          </FormControl>
        </Stack>

        <Typography variant='body2' color='text.secondary' mb={1}>
          Discount Rates
        </Typography>
        <DiscountRateBuilder
          onChange={rates => {
            discountRatesRef.current = rates
          }}
        />

        <Button
          variant='contained'
          fullWidth
          onClick={handleApprove}
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
          sx={{ mt: 2 }}
        >
          {isLoading ? 'Approving…' : 'Approve'}
        </Button>
      </Box>
    </Box>
  )
}

export default ApproveUserForm
